import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { List, X, ShareNetwork, Check, Plus, Minus } from '@phosphor-icons/react';
import { LanguageToggle } from './LanguageToggle';
import { useLanguage } from '../contexts/LanguageContext';

const LOGO_SRC = '/wsp-logo.png';

// (Msg 745) Host cities grouped by country for the mobile/tablet menu
// "Things To Do" expandable section. Each city links to /things-to-do
// with the city pre-selected via ?city= query param (the page reads the
// param and seeds the city picker filter).
const THINGS_TO_DO_CITIES = [
  { flag: '🇨🇦', country: 'Canada', cities: ['Toronto', 'Vancouver'] },
  { flag: '🇲🇽', country: 'Mexico', cities: ['Mexico City', 'Guadalajara', 'Monterrey'] },
  { flag: '🇺🇸', country: 'USA', cities: ['New York', 'Los Angeles', 'Boston', 'Miami', 'San Francisco', 'Atlanta', 'Dallas', 'Houston', 'Kansas City', 'Philadelphia', 'Seattle'] },
];

// (Msg 746) `FIRST_MATCH_END_UTC_MS` removed — Game Highlights now in
// ticker/mobile-menu/footer with no conditional reveal.

export const Header = () => {
  const { t } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [thingsToDoOpen, setThingsToDoOpen] = useState(false);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  // (Msg 746) `highlightsVisible` state removed — Game Highlights now lives
  // in the ticker (always-visible) + mobile menu + footer instead of a
  // conditional nav reveal.
  const [siteFans, setSiteFans] = useState(() => {
    try {
      const cached = parseInt(localStorage.getItem('wsp_site_fans') || '', 10);
      return Number.isFinite(cached) && cached > 0 ? cached : null;
    } catch { return null; }
  });
  const isAdmin = false;

  // Poll live site-fan count (same source feed the marquee + Advertise/Venue-Reg use)
  useEffect(() => {
    let cancelled = false;
    const fetchFans = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats/site-fans`);
        if (!r.ok) return;
        const d = await r.json();
        const n = d?.count ?? d?.site_fans ?? d?.value;
        if (!cancelled && Number.isFinite(n) && n > 0) {
          setSiteFans(n);
          try { localStorage.setItem('wsp_site_fans', String(n)); } catch {}
        }
      } catch {}
    };
    fetchFans();
    const id = setInterval(fetchFans, 60000); // refresh every minute
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Native Web Share with clipboard fallback. Same copy across mobile + desktop.
  const handleShareSite = async (e) => {
    if (e) e.preventDefault();
    const shareData = {
      title: 'World Soccer Party',
      text: "World Soccer Party is this summers things-to-watch local fan guide. Best events, good vibes and lots-of-soccer. We got you! Hit this link",
      url: typeof window !== 'undefined' ? window.location.origin : 'https://worldsoccerparty.com',
    };
    try {
      if (navigator?.share) {
        await navigator.share(shareData);
        return;
      }
    } catch { /* user cancelled the share sheet — ignore */ }
    try {
      await navigator.clipboard.writeText(`${shareData.text}\n${shareData.url}`);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2200);
    } catch { /* clipboard blocked — silent fail */ }
  };

  // Shrink header after scrolling past ~80px
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className="fixed left-0 right-0 z-50 top-0">
      <style>{`
        :root {
          --wsp-header-h: 80px;
          --wsp-header-h-scrolled: 56px;
        }
        @media (min-width: 640px) {
          :root { --wsp-header-h: 96px; --wsp-header-h-scrolled: 64px; }
        }
        @media (min-width: 1024px) {
          :root { --wsp-header-h: 180px; --wsp-header-h-scrolled: 90px; }
        }
      `}</style>
      <div className={`bg-white border-b-2 border-gray-200 transition-all duration-300 ${scrolled ? 'shadow-lg' : 'shadow-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* MOBILE / TABLET (< lg): single row with bigger, scroll-shrink logo */}
          <div className={`lg:hidden flex items-center justify-between gap-3 transition-all duration-300 ${scrolled ? 'h-14 sm:h-16' : 'h-20 sm:h-24'}`}>
            <Link to="/" className="flex-shrink-0" data-testid="logo-link-mobile">
              <img
                src={LOGO_SRC}
                alt="World Soccer Party"
                loading="eager"
                fetchpriority="high"
                decoding="async"
                width="1254"
                height="1254"
                className={`w-auto transition-all duration-300 ${scrolled ? 'h-10 sm:h-12' : 'h-16 sm:h-20'}`}
              />
            </Link>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* (Msg 753) Mobile language toggle — compact icon variant. */}
              <LanguageToggle variant="icon" />
              <button
                className="text-gray-800 p-2 flex-shrink-0"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-button"
                aria-label="Open menu"
              >
                {mobileMenuOpen ? <X size={30} weight="bold" /> : <List size={30} weight="bold" />}
              </button>
            </div>
          </div>

          {/* DESKTOP (>= lg): logo left, nav top right — scroll-shrinks from 180px → 90px */}
          <div className={`hidden lg:block relative transition-all duration-300 ${scrolled ? 'lg:h-[90px]' : 'lg:h-[180px]'}`}>
            <div className={`flex items-center justify-between transition-all duration-300 ${scrolled ? 'pt-3' : 'pt-[18px] items-start'}`}>
              <Link to="/" className="block" data-testid="logo-link">
                <img
                  src={LOGO_SRC}
                  alt="World Soccer Party"
                  loading="eager"
                  fetchpriority="high"
                  decoding="async"
                  width="1254"
                  height="1254"
                  className={`w-auto transition-all duration-300 ${isAdmin ? 'h-10' : (scrolled ? 'h-[68px]' : 'h-[140px]')}`}
                />
              </Link>
              <nav className="flex items-center gap-6" data-testid="desktop-nav">
                <Link to="/" className="text-gray-800 hover:text-[#FF6B35] transition-colors uppercase text-sm tracking-wider font-bold" data-testid="nav-home">{t('home')}</Link>
                <Link to="/events" className="relative inline-flex flex-col items-center text-[#FF6B35] hover:text-[#e85a24] transition-colors uppercase tracking-wider font-bold gap-1 leading-tight" data-testid="nav-listings">
                  <span className="text-sm">{t('eventsNearMe')}</span>
                  {siteFans !== null && siteFans > 0 && (
                    // (Msg 773) Live Stats pill — upgraded to high-contrast
                    // dark style for legibility. Was orange/tiny which the
                    // user reported as hard to read in the white nav bar.
                    <span className="inline-flex items-center gap-1.5 bg-[#0B1220] text-white px-2.5 py-1 rounded-full text-[11px] font-extrabold leading-none tabular-nums normal-case shadow-md ring-1 ring-black/10" data-testid="nav-listings-fans-badge" title="Live site fans">
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#10B981] opacity-75 animate-ping"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#10B981]"></span>
                      </span>
                      <span>{siteFans.toLocaleString()}</span>
                      <span className="text-[9px] tracking-wider opacity-80">LIVE</span>
                    </span>
                  )}
                </Link>
                <Link to="/match-schedule" className="text-gray-800 hover:text-[#FF6B35] transition-colors uppercase text-sm tracking-wider font-bold" data-testid="nav-match-schedule">{t('matchSchedule')}</Link>
                <Link to="/experiences" className="text-gray-800 hover:text-[#FF6B35] transition-colors uppercase text-sm tracking-wider font-bold" data-testid="nav-things-to-do">Things To Do</Link>
                {/* (Msg 746) Desktop nav DOES NOT show Game Highlights —
                    per user spec, desktop discovery is via the yellow ticker
                    pill + footer link. Mobile/tablet uses the hamburger menu.
                    (Msg 773) Blog hidden from desktop top nav — still
                    available via footer & mobile menu.
                    (Msg 785) "List Event Free" CTA moved from desktop top-nav
                    into the Footer to declutter the primary nav bar. */}
                <Link to="/partnerships" className="text-gray-800 hover:text-[#FF6B35] transition-colors uppercase text-sm tracking-wider font-bold" data-testid="nav-advertise">{t('advertise')}</Link>
                <Link to="/meet-the-team" className="text-gray-800 hover:text-[#FF6B35] transition-colors uppercase text-sm tracking-wider font-bold" data-testid="nav-about">{t('aboutUs')}</Link>
                {/* (Msg 753) Language toggle — pill variant on desktop. */}
                <LanguageToggle variant="pill" />
                <button
                  onClick={handleShareSite}
                  className="inline-flex items-center justify-center border-2 border-gray-300 hover:border-[#FF6B35] hover:text-[#FF6B35] text-gray-800 w-10 h-10 transition-all rounded-md"
                  data-testid="nav-share-site"
                  aria-label="Share World Soccer Party"
                  title="Share this site"
                >
                  {shareCopied ? <Check size={18} weight="bold" /> : <ShareNetwork size={18} weight="bold" />}
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile + Tablet Nav Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 bg-white z-50 shadow-2xl overflow-y-auto" style={{ top: scrolled ? '56px' : '80px' }} data-testid="mobile-menu">
          <nav className="flex flex-col px-6 py-6 gap-1">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="text-gray-800 hover:text-[#FF6B35] hover:bg-gray-50 transition-colors uppercase text-base tracking-wider font-bold py-4 px-3 rounded-lg border-b border-gray-100">{t('home')}</Link>
            <Link to="/events" onClick={() => setMobileMenuOpen(false)} className="text-[#FF6B35] hover:text-[#e85a24] hover:bg-orange-50 transition-colors uppercase text-base tracking-wider font-bold py-4 px-3 rounded-lg border-b border-gray-100">{t('eventsNearMe')}</Link>
            <Link to="/match-schedule" onClick={() => setMobileMenuOpen(false)} className="text-gray-800 hover:text-[#FF6B35] hover:bg-gray-50 transition-colors uppercase text-base tracking-wider font-bold py-4 px-3 rounded-lg border-b border-gray-100" data-testid="nav-match-schedule-mobile">{t('matchSchedule')}</Link>
            {/* (Msg 746) Game Highlights — auto-generated post-match recaps.
                Yellow tint matches the ticker pill for cross-surface recognition. */}
            <Link
              to="/blog/game-highlights"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-between text-gray-800 hover:text-[#FF6B35] hover:bg-gray-50 transition-colors uppercase text-base tracking-wider font-bold py-4 px-3 rounded-lg border-b border-gray-100"
              data-testid="nav-game-highlights-mobile"
            >
              <span>{t('gameHighlights')}</span>
              <span className="text-[9px] font-black uppercase tracking-widest bg-[#FFD700] text-[#0B1220] px-2 py-0.5 rounded-full">NEW</span>
            </Link>
            {/* (Msg 745) Things To Do — expandable section with 16 host
                cities, organized by country. Collapsed by default; tap +/− to
                reveal city sub-items. Each link pre-selects the city via
                /things-to-do?city=… */}
            <div className="border-b border-gray-100">
              <button
                type="button"
                onClick={() => setThingsToDoOpen(o => !o)}
                className="w-full flex items-center justify-between text-gray-800 hover:text-[#FF6B35] hover:bg-gray-50 transition-colors uppercase text-base tracking-wider font-bold py-4 px-3 rounded-lg"
                aria-expanded={thingsToDoOpen}
                data-testid="nav-things-to-do-toggle"
              >
                <span>Things To Do</span>
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-700">
                  {thingsToDoOpen ? <Minus size={14} weight="bold" /> : <Plus size={14} weight="bold" />}
                </span>
              </button>
              {thingsToDoOpen && (
                <div className="pl-3 pr-2 pb-3 space-y-3" data-testid="nav-things-to-do-cities">
                  <Link
                    to="/experiences"
                    onClick={() => { setMobileMenuOpen(false); setThingsToDoOpen(false); }}
                    className="block text-[13px] uppercase tracking-wider font-bold text-[#FF6B35] hover:underline py-1.5"
                    data-testid="nav-things-to-do-all"
                  >
                    All Cities →
                  </Link>
                  {THINGS_TO_DO_CITIES.map(group => (
                    <div key={group.country}>
                      <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-500 mb-1.5">{group.flag} {group.country}</p>
                      <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                        {group.cities.map(city => (
                          <li key={city}>
                            <Link
                              to={`/things-to-do?city=${encodeURIComponent(city)}`}
                              onClick={() => { setMobileMenuOpen(false); setThingsToDoOpen(false); }}
                              className="text-gray-700 hover:text-[#FF6B35] text-[13px]"
                              data-testid={`nav-things-to-do-${city.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              {city}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* (Msg 746) The old `/blog?category=game-highlights` mobile link
                was replaced above with the new `/blog/game-highlights` Link
                (right under Match Schedule). No duplicate here. */}
            <Link to="/blog" onClick={() => setMobileMenuOpen(false)} className="text-gray-800 hover:text-[#FF6B35] hover:bg-gray-50 transition-colors uppercase text-base tracking-wider font-bold py-4 px-3 rounded-lg border-b border-gray-100">{t('blog')}</Link>
            {/* (Msg 759) Partner with Us — expandable group consolidating
                Advertise, Affiliate, Get-a-Screen and List-an-Event into one
                tap-to-expand section. Removes the previous standalone
                Advertise/Affiliate mobile entries to avoid duplication. */}
            <div className="border-b border-gray-100">
              <button
                type="button"
                onClick={() => setPartnerOpen(o => !o)}
                className="w-full flex items-center justify-between text-gray-800 hover:text-[#FF6B35] hover:bg-gray-50 transition-colors uppercase text-base tracking-wider font-bold py-4 px-3 rounded-lg"
                aria-expanded={partnerOpen}
                data-testid="nav-partner-toggle-mobile"
              >
                <span>Partner with Us</span>
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-700">
                  {partnerOpen ? <Minus size={14} weight="bold" /> : <Plus size={14} weight="bold" />}
                </span>
              </button>
              {partnerOpen && (
                <ul className="pl-3 pr-2 pb-3 space-y-2" data-testid="nav-partner-sublinks-mobile">
                  <li>
                    <Link
                      to="/things-to-watch"
                      onClick={() => { setMobileMenuOpen(false); setPartnerOpen(false); }}
                      className="block text-gray-700 hover:text-[#FF6B35] text-[14px] py-1.5"
                      data-testid="nav-partner-get-a-screen-mobile"
                    >
                      Get a Screen
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/advertise"
                      onClick={() => { setMobileMenuOpen(false); setPartnerOpen(false); }}
                      className="block text-gray-700 hover:text-[#FF6B35] text-[14px] py-1.5"
                      data-testid="nav-partner-advertise-mobile"
                    >
                      Advertise with Us
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/venue-registration"
                      onClick={() => { setMobileMenuOpen(false); setPartnerOpen(false); }}
                      className="block text-gray-700 hover:text-[#FF6B35] text-[14px] py-1.5"
                      data-testid="nav-partner-list-event-mobile"
                    >
                      List an Event
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/affiliate"
                      onClick={() => { setMobileMenuOpen(false); setPartnerOpen(false); }}
                      className="block text-gray-700 hover:text-[#FF6B35] text-[14px] py-1.5"
                      data-testid="nav-partner-affiliate-mobile"
                    >
                      Become an Affiliate
                    </Link>
                  </li>
                </ul>
              )}
            </div>
            <Link to="/meet-the-team" onClick={() => setMobileMenuOpen(false)} className="text-gray-800 hover:text-[#FF6B35] hover:bg-gray-50 transition-colors uppercase text-base tracking-wider font-bold py-4 px-3 rounded-lg border-b border-gray-100">{t('aboutUs')}</Link>
            <Link to="/venue-registration" onClick={() => setMobileMenuOpen(false)} className="bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white px-6 py-4 font-bold uppercase tracking-wider text-center rounded-lg mt-4 text-base">{t('listYourEvent')}</Link>
            <button
              onClick={(e) => { handleShareSite(e); setMobileMenuOpen(false); }}
              className="border-2 border-gray-300 text-gray-800 px-6 py-4 font-bold uppercase tracking-wider text-center rounded-lg mt-2 text-base inline-flex items-center justify-center gap-2"
              data-testid="mobile-nav-share-site"
            >
              {shareCopied ? <Check size={20} weight="bold" /> : <ShareNetwork size={20} weight="bold" />}
              {shareCopied ? 'Link copied!' : 'Share This Site'}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};
